import { useCallback, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import type { AvailabilitySlot } from '@hub-wifi/shared'
import './room-timeline.css'

/**
 * Selection emitted by RoomTimeline. The ms values are absolute UTC epoch ms
 * that correspond to the building-local wall-clock of the selected cells —
 * i.e. `new Date(startMs)` rendered in `building.timezone` shows the cell's
 * start time. Parents converting these to ISO strings MUST use the building's
 * tz (see RoomBrowsePage.isoAtBuildingTz), NOT the browser's offset.
 */
export interface RoomTimelineSelection {
  startMs: number
  endMs: number
}

export interface RoomTimelineLabels {
  /** Cell state words used in aria-labels: "free" / "livre". */
  free: string
  /** "busy" / "ocupado". */
  busy: string
  /** "past" / "passado". */
  past: string
  /** "not bookable online" / "não reservável". */
  locked: string
  /** Range separator used between two HH:MM values: "to" / "às". */
  rangeSep: string
  /** Group label template: "availability for {room}" / "disponibilidade de {room}". */
  availabilityFor: string
  /** Live-region message for a completed selection. */
  selected: (range: string) => string
  /** Live-region message announcing the first click of a two-click selection. */
  startAnnouncement: (time: string) => string
}

export interface RoomTimelineProps {
  /** 30-min granularity, in the building's timezone. */
  slots: AvailabilitySlot[]
  /** Building-local 00:00 of the rendered day. Used for cell→time math. */
  dayStart: Date
  /** IANA timezone of the building. Used to render HH:MM labels correctly. */
  timezone: string
  /** Earliest cell to render (default 8 — i.e., 08:00). */
  startHour?: number
  /** Latest cell to render exclusive (default 20). */
  endHour?: number
  /** Active range selection for this room. Null = no selection. */
  selection: RoomTimelineSelection | null
  /** Called when the user requests a new selection — tap/click sequence OR drag. */
  onSelect: (next: RoomTimelineSelection | null) => void
  /** Greys out + locks every cell. Used for non-bookable rooms. */
  disabled?: boolean
  /** Aria-label prefix for "<roomName> · <cellTime> · <free|busy>" announcements. */
  roomName: string
  /** i18n strings for cell aria-labels and live-region announcements. */
  labels: RoomTimelineLabels
  /**
   * Absolute epoch ms used for both "past cell" computation AND the live
   * "now" indicator. Parents that want the indicator to refresh should hold
   * this in state and update it on an interval (`RoomBrowsePage` does this
   * every 60 s). Defaults to `Date.now()` for hosts that don't care.
   *
   * Backward-compat: a legacy `() => number` test seam is also accepted; the
   * component normalizes both shapes to an epoch ms internally.
   */
  now?: number | (() => number)
}

const CELL_MINUTES = 30
const CELL_MS = CELL_MINUTES * 60_000
const DRAG_THRESHOLD_PX = 4

type CellState = 'free' | 'busy' | 'past' | 'locked'

interface RenderedCell {
  index: number
  startMs: number
  endMs: number
  state: CellState
  label: string
}

function formatHm(ms: number, timezone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(ms))
}

/**
 * Map an availability slot list onto the [startHour, endHour) window for the
 * day. Slots outside the window are ignored. Missing slots default to `busy`
 * — better to over-block than over-promise. Also returns the integer-hour
 * `axisLabels` (one per hour in the window) used to render the visible time
 * axis above the strip.
 */
function buildCells(
  slots: AvailabilitySlot[],
  dayStart: Date,
  timezone: string,
  startHour: number,
  endHour: number,
  disabled: boolean,
  nowMs: number,
  rangeSep: string,
): { cells: RenderedCell[]; axisLabels: string[] } {
  const dayStartMs = dayStart.getTime()
  const windowStartMs = dayStartMs + startHour * 60 * 60_000
  const windowEndMs = dayStartMs + endHour * 60 * 60_000
  const slotByStartMs = new Map<number, AvailabilitySlot>()
  for (const s of slots) {
    slotByStartMs.set(new Date(s.startAt).getTime(), s)
  }
  const cells: RenderedCell[] = []
  for (let cellStart = windowStartMs, i = 0; cellStart < windowEndMs; cellStart += CELL_MS, i++) {
    const cellEnd = cellStart + CELL_MS
    const slot = slotByStartMs.get(cellStart)
    let state: CellState
    if (disabled) {
      state = 'locked'
    } else if (cellEnd <= nowMs) {
      state = 'past'
    } else if (!slot || !slot.free) {
      state = 'busy'
    } else {
      state = 'free'
    }
    cells.push({
      index: i,
      startMs: cellStart,
      endMs: cellEnd,
      state,
      label: `${formatHm(cellStart, timezone)} ${rangeSep} ${formatHm(cellEnd, timezone)}`,
    })
  }
  // One label per integer hour in [startHour, endHour). `formatHm` returns
  // `HH:MM`; drop the `:MM` since the axis only shows the hour.
  const axisLabels: string[] = []
  for (let h = startHour; h < endHour; h++) {
    const ms = dayStartMs + h * 3_600_000
    axisLabels.push(formatHm(ms, timezone).slice(0, 2))
  }
  return { cells, axisLabels }
}

/**
 * Clamp the requested range [originIdx, targetIdx] to the longest contiguous
 * free run starting at originIdx, in the direction of targetIdx. The origin
 * cell is always included.
 */
function clampToFreeRun(
  cells: RenderedCell[],
  originIdx: number,
  targetIdx: number,
): { startIdx: number; endIdx: number } {
  if (originIdx === targetIdx) return { startIdx: originIdx, endIdx: originIdx }
  const dir = targetIdx > originIdx ? 1 : -1
  let last = originIdx
  for (let i = originIdx + dir; dir > 0 ? i <= targetIdx : i >= targetIdx; i += dir) {
    if (cells[i]?.state !== 'free') break
    last = i
  }
  const lo = Math.min(originIdx, last)
  const hi = Math.max(originIdx, last)
  return { startIdx: lo, endIdx: hi }
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'maybe_click'; originIdx: number; pointerId: number; x: number; y: number }
  | { kind: 'dragging'; originIdx: number; pointerId: number }
  | { kind: 'start'; originIdx: number }

export function RoomTimeline(props: RoomTimelineProps): JSX.Element {
  const {
    slots,
    dayStart,
    timezone,
    startHour = 8,
    endHour = 20,
    selection,
    onSelect,
    disabled = false,
    roomName,
    labels,
    now,
  } = props

  // Normalize the `now` prop. Accept `number` (preferred; lets parents push
  // a tick every 60 s) and `() => number` (legacy test seam). Falling back to
  // `Date.now()` keeps callers that don't care about the indicator working.
  const rawNow = typeof now === 'function' ? now() : now
  const nowMs = typeof rawNow === 'number' && Number.isFinite(rawNow) ? rawNow : Date.now()

  // Defensive validity flag, evaluated BEFORE hooks. Used to short-circuit
  // render AFTER all hooks have been called (Rules of Hooks). Parents are
  // expected to pass `startHour < endHour` and a non-NaN `dayStart`.
  const isValid = endHour > startHour && !Number.isNaN(dayStart.getTime())

  const { cells, axisLabels } = useMemo(
    () =>
      buildCells(slots, dayStart, timezone, startHour, endHour, disabled, nowMs, labels.rangeSep),
    [slots, dayStart, timezone, startHour, endHour, disabled, nowMs, labels.rangeSep],
  )

  // The "now" indicator is rendered only when (a) `nowMs` lands inside the
  // [startHour, endHour) window for the rendered day AND (b) `nowMs` is on
  // the same calendar date as `dayStart` in the building's timezone. The
  // second guard prevents the indicator from showing on a day that is not
  // today (e.g. user clicked the Tomorrow chip).
  const showNow = useMemo(() => {
    const dayStartMs = dayStart.getTime()
    const windowStartMs = dayStartMs + startHour * 3_600_000
    const windowEndMs = dayStartMs + endHour * 3_600_000
    if (nowMs < windowStartMs || nowMs >= windowEndMs) return false
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    return fmt.format(new Date(nowMs)) === fmt.format(dayStart)
  }, [nowMs, dayStart, startHour, endHour, timezone])

  const nowFraction = useMemo(() => {
    if (!showNow) return 0
    const dayStartMs = dayStart.getTime()
    const windowStartMs = dayStartMs + startHour * 3_600_000
    const windowMs = (endHour - startHour) * 3_600_000
    const f = (nowMs - windowStartMs) / windowMs
    return Math.min(1, Math.max(0, f))
  }, [showNow, nowMs, dayStart, startHour, endHour])
  const phaseRef = useRef<Phase>({ kind: 'idle' })
  const [liveMessage, setLiveMessage] = useState('')
  const [invalidFlashIdx, setInvalidFlashIdx] = useState<number | null>(null)

  const selectionCellIdxs = useMemo(() => {
    if (!selection) return null
    const lo = cells.findIndex((c) => c.startMs === selection.startMs)
    const hi = cells.findIndex((c) => c.endMs === selection.endMs)
    if (lo < 0 || hi < 0) return null
    return { lo, hi }
  }, [selection, cells])

  const emitSelection = useCallback(
    (lo: number, hi: number) => {
      const startMs = cells[lo]!.startMs
      const endMs = cells[hi]!.endMs
      onSelect({ startMs, endMs })
      const startLabel = formatHm(startMs, timezone)
      const endLabel = formatHm(endMs, timezone)
      setLiveMessage(labels.selected(`${startLabel} ${labels.rangeSep} ${endLabel}`))
    },
    [cells, timezone, onSelect, labels],
  )

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>, idx: number) => {
      if (disabled) return
      const cell = cells[idx]!
      if (cell.state === 'busy' || cell.state === 'past') {
        setInvalidFlashIdx(idx)
        setTimeout(() => setInvalidFlashIdx(null), 200)
        return
      }
      // Preserve START — this is the second click's pointerdown of a two-click
      // selection. pointerup will read its own target cell to compute the end
      // and complete the range. If we transitioned to MAYBE_CLICK here we'd
      // clobber the originIdx left by the first click's pointerup.
      if (phaseRef.current.kind === 'start') {
        return
      }
      ;(e.target as Element).setPointerCapture?.(e.pointerId)
      phaseRef.current = {
        kind: 'maybe_click',
        originIdx: idx,
        pointerId: e.pointerId,
        x: e.clientX,
        y: e.clientY,
      }
    },
    [cells, disabled],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>, idx: number) => {
      const phase = phaseRef.current
      if (phase.kind === 'maybe_click' && phase.pointerId === e.pointerId) {
        const dx = e.clientX - phase.x
        const dy = e.clientY - phase.y
        if (Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX) {
          phaseRef.current = {
            kind: 'dragging',
            originIdx: phase.originIdx,
            pointerId: e.pointerId,
          }
        } else {
          return
        }
      }
      if (phaseRef.current.kind === 'dragging' && phaseRef.current.pointerId === e.pointerId) {
        const { startIdx, endIdx } = clampToFreeRun(cells, phaseRef.current.originIdx, idx)
        emitSelection(startIdx, endIdx)
      }
    },
    [cells, emitSelection],
  )

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>, idx: number) => {
      const phase = phaseRef.current
      if (phase.kind === 'maybe_click' && phase.pointerId === e.pointerId) {
        // Tap: first click — START state.
        if (selection) {
          // Already had a selection; a fresh tap starts over.
          phaseRef.current = { kind: 'start', originIdx: idx }
          emitSelection(idx, idx)
        } else if (phaseRef.current.kind !== 'idle') {
          phaseRef.current = { kind: 'start', originIdx: phase.originIdx }
          emitSelection(phase.originIdx, phase.originIdx)
          setLiveMessage(
            labels.startAnnouncement(formatHm(cells[phase.originIdx]!.startMs, timezone)),
          )
        }
        return
      }
      if (phaseRef.current.kind === 'dragging') {
        phaseRef.current = { kind: 'idle' }
        return
      }
      if (phase.kind === 'start') {
        const cell = cells[idx]!
        if (cell.state !== 'free') {
          setInvalidFlashIdx(idx)
          setTimeout(() => setInvalidFlashIdx(null), 200)
          phaseRef.current = { kind: 'idle' }
          onSelect(null)
          return
        }
        const { startIdx, endIdx } = clampToFreeRun(cells, phase.originIdx, idx)
        emitSelection(startIdx, endIdx)
        phaseRef.current = { kind: 'idle' }
      }
    },
    [cells, timezone, emitSelection, onSelect, selection, labels],
  )

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, idx: number) => {
      if (disabled) return
      if (e.key !== 'Enter' && e.key !== ' ') return
      e.preventDefault()
      const cell = cells[idx]!
      if (cell.state !== 'free') return
      const phase = phaseRef.current
      if (phase.kind === 'start') {
        const { startIdx, endIdx } = clampToFreeRun(cells, phase.originIdx, idx)
        emitSelection(startIdx, endIdx)
        phaseRef.current = { kind: 'idle' }
      } else {
        phaseRef.current = { kind: 'start', originIdx: idx }
        emitSelection(idx, idx)
        setLiveMessage(labels.startAnnouncement(formatHm(cell.startMs, timezone)))
      }
    },
    [cells, timezone, disabled, emitSelection, labels],
  )

  if (!isValid) {
    // Invalid window (endHour <= startHour) or NaN dayStart — render an
    // empty strip rather than risk computing on degenerate inputs.
    return <div className="room-timeline" data-testid="room-timeline" />
  }

  return (
    <div className="room-timeline" data-testid="room-timeline">
      <div className="room-timeline__axis" aria-hidden="true" data-testid="room-timeline-axis">
        {axisLabels.map((label, i) => (
          <span key={`${label}-${i}`} className="room-timeline__axis-label">
            {label}
          </span>
        ))}
      </div>
      <div
        className="room-timeline__strip"
        style={{ touchAction: 'none', userSelect: 'none' }}
        role="group"
        aria-label={labels.availabilityFor.replace('{room}', roomName)}
      >
        {showNow && (
          <div
            className="room-timeline__now"
            style={{ left: `${nowFraction * 100}%` }}
            data-testid="room-timeline-now"
            aria-hidden="true"
          />
        )}
        {cells.map((cell) => {
          const inRange =
            selectionCellIdxs !== null &&
            cell.index >= selectionCellIdxs.lo &&
            cell.index <= selectionCellIdxs.hi
          const isStart = selectionCellIdxs !== null && cell.index === selectionCellIdxs.lo
          const classes = [
            'room-timeline__cell',
            `room-timeline__cell--${cell.state}`,
            inRange
              ? isStart
                ? 'room-timeline__cell--selected-start'
                : 'room-timeline__cell--selected-range'
              : '',
            invalidFlashIdx === cell.index ? 'room-timeline__cell--selection-invalid' : '',
          ]
            .filter(Boolean)
            .join(' ')
          const isClickable = !disabled && cell.state === 'free'
          const stateWord =
            cell.state === 'free'
              ? labels.free
              : cell.state === 'busy'
                ? labels.busy
                : cell.state === 'past'
                  ? labels.past
                  : labels.locked
          return (
            <button
              key={cell.index}
              type="button"
              className={classes}
              data-testid={`cell-${cell.index}`}
              data-state={cell.state}
              disabled={!isClickable}
              aria-label={`${roomName} · ${cell.label} · ${stateWord}`}
              onPointerDown={(e) => onPointerDown(e, cell.index)}
              onPointerMove={(e) => onPointerMove(e, cell.index)}
              onPointerUp={(e) => onPointerUp(e, cell.index)}
              onKeyDown={(e) => onKeyDown(e, cell.index)}
            >
              <span className="sr-only">{cell.label}</span>
            </button>
          )
        })}
      </div>
      <div role="status" aria-live="polite" className="room-timeline__live sr-only">
        {liveMessage}
      </div>
    </div>
  )
}
